import React, { useCallback, useEffect, useState } from 'react';
import { SectionBase } from './SectionBase';
import styled from 'styled-components';
import { getYearFromRow } from '../utils';
import { Program } from './SectionPrograms';
import Selector, {
  MAX_WIDTH_FOR_SELECTOR,
} from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { GuidanceTextResponse } from './utils';

interface Value {
  id: string;
  value: 'Yes' | 'No';
}

interface SelectedValues {
  [x: string]: Value['value'];
}

const currentYear = new Date().getFullYear();

interface SectionProgramsYesNoProps {
  title: string;
  subTitle: string;
  helpText: string;
  questionId: number;
  guidanceText: GuidanceTextResponse;
  onChange: (questionId: number, value: Value[]) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  options: Program[];
  value: Value[] | undefined;
}

const isPeriodNotSet = (period: string | undefined) => {
  return !period || period === 'Period not set...';
};

const parseValue = (value: Value[], programs: Program[]): SelectedValues => {
  const result: SelectedValues = {};
  value.forEach(x => {
    result[x.id] = x.value;
  });
  programs.forEach(x => {
    if (isPeriodNotSet(x.period)) {
      x.period = currentYear.toString();
      x.year = currentYear;
    }
  });
  return result;
};

export const SectionProgramsYesNo: React.FC<SectionProgramsYesNoProps> = ({
  title,
  subTitle,
  helpText,
  options,
  questionId,
  guidanceText,
  value = [],
  onChange,
  error,
}) => {
  const [selected, setSelected] = useState<SelectedValues>(
    parseValue(value, options),
  );

  useEffect(() => {
    const selectedKeys = Object.keys(selected);
    if (selectedKeys.length < options.length) {
      onChange(questionId, []);
      return;
    }
    const value: Value[] = selectedKeys.map(k => ({
      id: k,
      value: selected[k],
    }));
    onChange(questionId, value);
  }, [selected, onChange, questionId]);

  const handleChange = useCallback(
    (programId: string | number, value: Value['value']) => {
      setSelected(s => ({ ...s, [programId.toString()]: value }));
    },
    [questionId, onChange, setSelected],
  );

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={helpText}
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      {options.length > 1 && (
        <StyledP>
          <label className="title-6">Select all that apply.</label>
        </StyledP>
      )}
      {options.map(opt => {
        const periodNotSet = isPeriodNotSet(opt.period);
        return (
          <Group>
            <div className="box">
              <p>
                <label className="title-5">{opt.name}</label>
              </p>
              <p>
                <label className="regular-3">{getYearFromRow(opt)}</label>
              </p>
            </div>
            <div className="options-wrapper">
              <Selector
                variant="secondary"
                label="Yes"
                selected={selected[opt.id] === 'Yes'}
                maxWidth={MAX_WIDTH_FOR_SELECTOR}
                padding={SPACERS.tiny}
                margin={`${SPACERS.tiny} auto`}
                onClick={() => handleChange(opt.id, 'Yes')}
                disabled={periodNotSet}
                titleText={
                  periodNotSet
                    ? 'Period is not set for this program'
                    : undefined
                }
                className="option-yes"
              />
              <Selector
                variant="secondary"
                label="No"
                selected={selected[opt.id] === 'No'}
                padding={SPACERS.tiny}
                maxWidth={MAX_WIDTH_FOR_SELECTOR}
                margin={`${SPACERS.tiny} auto`}
                onClick={() => handleChange(opt.id, 'No')}
                className="option-no"
              />
            </div>
          </Group>
        );
      })}
    </SectionBase>
  );
};

const StyledP = styled.p`
  margin-top: 16px;
  margin-bottom: 16px;
`;

const Group = styled.div`
  width: 100%;
  margin-bottom: ${SPACERS.tiny};
  .options-wrapper {
    display: flex;
    .option-yes,
    .option-no {
      height: auto !important;
      justify-content: center;
    }
    .option-yes {
      margin-right: ${SPACERS.tiny};
    }
    .option-no {
      margin-left: ${SPACERS.tiny};
    }
  }
  .box {
    width: 100%;
    margin-top: 10px;
    min-height: 40px;
    padding-left: 15px;
    padding-right: 15px;
    border: solid 1px #cbd6e2;
    border-radius: 4px;
    background-color: #ffffff;
    p {
      margin-block-start: 0.4em;
      margin-block-end: 0.4em;
    }
  }
`;
